import { useEffect, useRef, useState } from "react";
import { analyse, buildWorld, heightAt, locate, sample, toWorld, LENGTH, STRIDE } from "./world.js";
import { COLORS, ORDER, SHOTS, lights, overlay, prepare, readout } from "./steps.js";
import { createPoints } from "./pointsGL.js";
import CodeChip from "../CodeChip/CodeChip.jsx";
import styles from "./AnalysisScene.module.css";

const BASE = [196, 212, 228];
const EASE = 0.12; // per-frame easing of the camera toward the scroll position
const REVEAL_SPEED = 380; // m/s the scan sweeps in at, on arrival
const REVEAL_AT = 0.3; // share of the scene on screen that starts the sweep
const FADE = 0.9; // s for an analysis's overlay to fade in
// Without WebGL the points go on the 2D canvas, one call each; past THIN_FROM metres, where
// grey points pile up on screen, it draws a (THIN_FROM / distance)² share of them (at least
// THIN_MIN), a little larger. Highlights always draw.
const THIN_FROM = 110;
const THIN_MIN = 0.35;

const LEVELS = 12; // brightness steps per colour
const ALPHAS = 8; // opacity steps per brightness

function palette([r, g, b]) {
  const out = [];
  for (let i = 0; i < LEVELS; i++) {
    const row = [];
    const k = 0.22 + (0.78 * i) / (LEVELS - 1);
    for (let a = 0; a < ALPHAS; a++) {
      row.push(
        `rgba(${Math.round(r * k)},${Math.round(g * k)},${Math.round(b * k)},${((a + 1) / ALPHAS).toFixed(3)})`
      );
    }
    out.push(row);
  }
  return out;
}
const PALETTES = [palette(BASE), ...COLORS.slice(1).map(palette)];
// The 2D canvas draws its points grouped by these swatches, setting each fill once rather
// than once per point, so it can batch the squares.
const SWATCHES = PALETTES.length * LEVELS * ALPHAS;

const clamp01 = (t) => Math.max(0, Math.min(1, t));
const lerp = (a, b, t) => a + (b - a) * t;
/** Between two shots the camera holds still for the first and last 30%, then moves. */
const hold = (t) => {
  const u = clamp01((t - 0.3) / 0.4);
  return u * u * (3 - 2 * u);
};
const mixPoint = (a, b, t) => ({
  s: lerp(a.s, b.s, t),
  o: lerp(a.o, b.o, t),
  h: lerp(a.h, b.h, t),
});
const mixShot = (a, b, t) => ({
  cam: mixPoint(a.cam, b.cam, t),
  look: mixPoint(a.look, b.look, t),
});
const gap = (a, b) =>
  Math.abs(a.cam.s - b.cam.s) +
  Math.abs(a.cam.o - b.cam.o) +
  Math.abs(a.cam.h - b.cam.h) +
  Math.abs(a.look.s - b.look.s) +
  Math.abs(a.look.o - b.look.o);

/**
 * The 123BIMLive analyses on a dense simulated LiDAR corridor. Scroll moves the camera from
 * one analysis's shot to the next (`progressRef`, 0-1); `phase` is the analysis on show, and
 * its points light up with an overlay and a readout beside them. The corridor and every
 * number come from world.js; the page says the road is simulated. `textEdge` is where the
 * step text beside the scene ends (px from its left edge): each shot is framed right of it.
 * The points draw with WebGL (pointsGL.js), or on the 2D canvas where there is none; the
 * overlay's lines and labels go on the 2D canvas over them.
 */
export default function AnalysisScene({ progressRef, phase = 0, textEdge = 0, className = "" }) {
  const wrapRef = useRef(null);
  const glRef = useRef(null);
  const canvasRef = useRef(null);
  const phaseRef = useRef(phase);
  const edgeRef = useRef(textEdge);
  const [results, setResults] = useState(null);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    edgeRef.current = textEdge;
  }, [textEdge]);

  useEffect(() => {
    const host = wrapRef.current;
    const glCanvas = glRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const reduced =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.body.classList.contains("reduced-motion");

    let world = null;
    let r = null;
    let glPoints = null;
    const lit = {};
    const lightsFor = (key) => (lit[key] ||= lights(key, world, r));
    // 2D only. Per point drawn this frame: its square (corner x, y and size) and swatch;
    // `starts` indexes `order` by swatch (a counting sort, so each swatch keeps far to near).
    let sqX = null;
    let sqY = null;
    let sqSize = null;
    let sqSwatch = null;
    let order = null;
    const starts = new Int32Array(SWATCHES + 1);
    const cursor = new Int32Array(SWATCHES);
    let w = 0;
    let h = 0;
    let dpr = 1;
    let dirty = true;
    let cur = SHOTS[ORDER[0]];
    let shownEdge = -1;
    let shownPhase = -1;
    let phaseAt = 0;
    let revealFrom = reduced ? 0 : null;
    let raf = 0;
    let running = false;
    let onScreen = false;
    let built = false;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      glCanvas.width = canvas.width;
      glCanvas.height = canvas.height;
      dirty = true;
    };
    const fallBackTo2D = () => {
      glPoints = null;
      if (sqX) return;
      sqX = new Float32Array(world.count);
      sqY = new Float32Array(world.count);
      sqSize = new Float32Array(world.count);
      sqSwatch = new Uint16Array(world.count);
      order = new Int32Array(world.count);
    };
    const onContextLost = (e) => {
      e.preventDefault();
      if (world) fallBackTo2D();
      dirty = true;
    };
    glCanvas.addEventListener("webglcontextlost", onContextLost);

    // Without WebGL: one square per point on the 2D canvas, grouped by swatch.
    const drawPoints2D = (f) => {
      const P = world.points;
      const [px, py, pz] = f.cam;
      const [fx, fy, fz] = f.forward;
      const [rx, , rz] = f.right;
      const [ux, uy, uz] = f.up;
      const hiOf = f.lights ? f.lights.hi : null;
      const alongOf = f.lights ? f.lights.along : null;
      starts.fill(0);
      let count = 0;
      for (let j = f.last - 1; j >= f.first; j--) {
        const i = j * STRIDE;
        const vx = P[i] - px;
        const vy = P[i + 1] - py;
        const vz = P[i + 2] - pz;
        const zc = vx * fx + vy * fy + vz * fz;
        if (zc < 0.6 || zc > f.far || zc > f.reveal) continue;
        const sx = f.cx + ((vx * rx + vz * rz) / zc) * f.focal;
        if (sx < -3 || sx > f.w + 3) continue;
        const sy = f.cy - ((vx * ux + vy * uy + vz * uz) / zc) * f.focal;
        if (sy < -3 || sy > f.h + 3) continue;
        const hi = hiOf && hiOf[j] && alongOf[j] <= f.reach ? hiOf[j] : 0;
        let grow = 1;
        if (!hi && zc > THIN_FROM) {
          const keep = Math.max(THIN_MIN, (THIN_FROM / zc) ** 2);
          // The same points drop out every frame: a hash of the index, not a random draw.
          if ((Math.imul(j, 2654435761) >>> 0) / 4294967296 >= keep) continue;
          grow = keep ** -0.25;
        }
        let fade = Math.min(1, zc / 2.5) * Math.min(1, (f.far - zc) / (f.far * 0.45));
        const front = f.reveal - zc;
        if (front < 20) fade = Math.min(1, fade + (1 - front / 20) * 0.5);
        const inten = P[i + 3];
        let level;
        if (hi) {
          level = Math.round((0.5 + inten * 0.5) * (LEVELS - 1));
          fade = Math.min(1, fade * 1.25);
        } else {
          level = Math.round(inten * (LEVELS - 1) * f.dim);
        }
        const alpha = Math.max(0, Math.min(ALPHAS - 1, Math.round(fade * (ALPHAS - 1))));
        if (alpha === 0 && fade < 0.06) continue;
        const size = Math.max(1.2, Math.min(2.6, f.sizeK / zc)) * grow + (hi ? 0.3 : 0);
        const swatch = (hi * LEVELS + level) * ALPHAS + alpha;
        sqX[count] = sx - size / 2;
        sqY[count] = sy - size / 2;
        sqSize[count] = size;
        sqSwatch[count] = swatch;
        starts[swatch + 1]++;
        count++;
      }
      // Grey first, then the highlights over it; within a swatch, far to near.
      for (let k = 0; k < SWATCHES; k++) starts[k + 1] += starts[k];
      cursor.set(starts.subarray(0, SWATCHES));
      for (let m = 0; m < count; m++) order[cursor[sqSwatch[m]]++] = m;
      for (let k = 0; k < SWATCHES; k++) {
        const end = starts[k + 1];
        if (starts[k] === end) continue;
        const shade = PALETTES[(k / (LEVELS * ALPHAS)) | 0][((k / ALPHAS) | 0) % LEVELS];
        ctx.fillStyle = shade[k % ALPHAS];
        for (let q = starts[k]; q < end; q++) {
          const m = order[q];
          ctx.fillRect(sqX[m], sqY[m], sqSize[m], sqSize[m]);
        }
      }
    };

    const draw = (now) => {
      if (!world) return;
      const n = ORDER.length;
      const ph = Math.max(0, Math.min(n - 1, phaseRef.current));
      if (ph !== shownPhase) {
        shownPhase = ph;
        phaseAt = now;
        dirty = true;
      }
      const edge = edgeRef.current;
      if (edge !== shownEdge) {
        shownEdge = edge;
        dirty = true;
      }
      // Where the camera should be: at the active shot, or on its way to the next one.
      let target;
      if (reduced) target = SHOTS[ORDER[ph]];
      else {
        const pos = clamp01(progressRef.current || 0) * (n - 1);
        const i = Math.min(n - 2, Math.floor(pos));
        target = mixShot(SHOTS[ORDER[i]], SHOTS[ORDER[i + 1]], hold(pos - i));
      }
      const moving = gap(cur, target) > 0.01;
      cur = reduced ? target : mixShot(cur, target, EASE);
      const age = (now - phaseAt) / 1000;
      const key = ORDER[ph];
      const fading = !reduced && age < 4;
      const revealing = revealFrom !== null && !reduced && (now - revealFrom) / 1000 < 2;
      if (!dirty && !moving && !fading && !revealing) return;
      dirty = false;

      const al = world.al;
      const yAt = (p) => sample(al, p.s).y + p.h;
      const C = toWorld(al, cur.cam.s, cur.cam.o);
      const L = toWorld(al, cur.look.s, cur.look.o);
      const px = C.x;
      const py = yAt(cur.cam);
      const pz = C.z;
      let fx = L.x - px;
      let fy = yAt(cur.look) - py;
      let fz = L.z - pz;
      const fl = Math.hypot(fx, fy, fz);
      fx /= fl;
      fy /= fl;
      fz /= fl;
      let rx = fz;
      let rz = -fx;
      const rl = Math.hypot(rx, rz);
      rx /= rl;
      rz /= rl;
      const ux = fy * rz;
      const uy = fz * rx - fx * rz;
      const uz = -fy * rx;

      // Beside a text column the shot is framed in the width right of it, a little left of
      // that width's middle; with the text below (phones), in the top half of the screen.
      const side = w >= 768 && edge > 0 && edge < w * 0.62;
      const left = side ? edge : 0;
      const cx = side ? left + (w - left) * 0.37 : w * 0.5;
      const cy = h * (side ? 0.46 : 0.32);
      const focal = side ? (w - left) * 1.05 : Math.max(w, h) * 0.62;
      const aerial = cur.cam.h > 25;
      const far = aerial ? 900 : 360;
      const reveal =
        revealFrom === null ? 0 : reduced ? Infinity : ((now - revealFrom) / 1000) * REVEAL_SPEED;
      const mix = reduced ? 1 : clamp01(age / FADE);
      const light = r ? lightsFor(key) : null;
      const reach = reduced || !light || !light.speed ? Infinity : age * light.speed;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const sLo = aerial ? cur.look.s - 300 : Math.min(cur.cam.s, cur.look.s) - 30;
      const sHi = aerial ? cur.look.s + 300 : Math.max(cur.cam.s, cur.look.s) + 320;
      const first = world.start[Math.max(0, Math.floor(sLo))];
      const last = world.start[Math.min(LENGTH, Math.floor(sHi) + 1)];
      // Points stay 1.2-2.6 px across the view, as a scan reads: size by depth alone made
      // everything past 30 m sub-pixel and the corridor all but vanished.
      const sizeK = focal * 0.3;
      const dim = light ? 1 - 0.28 * mix : 1;
      const frame = {
        lights: light,
        cam: [px, py, pz],
        forward: [fx, fy, fz],
        right: [rx, 0, rz],
        up: [ux, uy, uz],
        cx,
        cy,
        w,
        h,
        dpr,
        focal,
        far,
        reveal,
        reach,
        dim,
        sizeK,
        first,
        last,
      };
      if (glPoints) glPoints.draw(frame);
      else drawPoints2D(frame);

      if (r && mix > 0) {
        const project = (x, y, z) => {
          const vx = x - px;
          const vy = y - py;
          const vz = z - pz;
          const zc = vx * fx + vy * fy + vz * fz;
          if (zc < 0.5) return null;
          return {
            x: cx + ((vx * rx + vz * rz) / zc) * focal,
            y: cy - ((vx * ux + vy * uy + vz * uz) / zc) * focal,
          };
        };
        const v = {
          w,
          h,
          left,
          al,
          world: project,
          abs: (s, o, y) => {
            const q = toWorld(al, s, o);
            return project(q.x, y, q.z);
          },
          at: (s, o, above) => {
            const q = toWorld(al, s, o);
            return project(q.x, heightAt(al, s, o) + above, q.z);
          },
          profile: (s) => sample(al, s).y,
          locate: (x, z) => locate(al, x, z, cur.look.s, 80),
          // A direction (azimuth from the corridor's +z, elevation) far off, as the sky shows it.
          dir: (az, el) =>
            project(
              px + Math.sin(az) * Math.cos(el) * 4000,
              py + Math.sin(el) * 4000,
              pz + Math.cos(az) * Math.cos(el) * 4000
            ),
        };
        ctx.save();
        ctx.globalAlpha = mix;
        overlay(key, ctx, v, r);
        ctx.restore();
      }
    };

    const loop = (now) => {
      draw(now);
      raf = requestAnimationFrame(loop);
    };
    const play = () => {
      if (running || !built) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const pause = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    // Build the corridor only when the story is about to be seen: it is ~170k points.
    const build = () => {
      if (built) return;
      built = true;
      const density = window.innerWidth < 768 ? 0.45 : 1;
      world = buildWorld({ density });
      r = prepare(world, analyse(world));
      glPoints = createPoints(glCanvas, world, [BASE, ...COLORS.slice(1)]);
      if (!glPoints) fallBackTo2D();
      setResults(r);
      resize();
      if (reduced) draw(performance.now());
      else if (onScreen) play();
    };
    const near = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          near.disconnect();
          setTimeout(build, 0);
        }
      },
      { rootMargin: "1200px 0px" }
    );
    near.observe(host);

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        if (revealFrom === null && entry.intersectionRatio >= REVEAL_AT) {
          revealFrom = performance.now();
          dirty = true;
        }
        if (onScreen && !document.hidden) play();
        else pause();
      },
      { threshold: [0, REVEAL_AT] }
    );
    io.observe(host);
    const ro = new ResizeObserver(() => {
      resize();
      if (reduced) draw(performance.now());
    });
    ro.observe(host);
    const onVisibility = () => (document.hidden || !onScreen ? pause() : play());
    document.addEventListener("visibilitychange", onVisibility);
    // Reduced motion draws each step once, as a still; redraw when the step or framing changes.
    const redraw = reduced
      ? setInterval(() => {
          if (world && (phaseRef.current !== shownPhase || edgeRef.current !== shownEdge))
            draw(performance.now());
        }, 150)
      : 0;

    return () => {
      pause();
      near.disconnect();
      io.disconnect();
      ro.disconnect();
      clearInterval(redraw);
      document.removeEventListener("visibilitychange", onVisibility);
      glCanvas.removeEventListener("webglcontextlost", onContextLost);
    };
  }, [progressRef]);

  const info = results
    ? readout(ORDER[Math.max(0, Math.min(ORDER.length - 1, phase))], results)
    : null;
  return (
    <div ref={wrapRef} className={`${styles.scene} ${className}`.trim()} aria-hidden="true">
      <canvas ref={glRef} className={styles.canvas} />
      <canvas ref={canvasRef} className={styles.canvas} />
      {info ? (
        <div className={styles.hud} style={{ "--tint": info.tint }}>
          <p className={styles.hudHead}>
            <CodeChip code={info.code} tint={info.tint} />
            <span className={styles.hudTitle}>{info.title}</span>
          </p>
          <p className={styles.summary}>{info.summary}</p>
          <dl className={styles.readout}>
            {info.rows.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <p className={styles.status} data-tone={info.status[1]}>
            {info.status[0]}
          </p>
        </div>
      ) : null}
    </div>
  );
}
